import { ChangeDetectorRef, Injectable, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { Subject, takeUntil } from 'rxjs';

import { AuthorizationService, EntryPointService, LogService, MagnetService, TranslationService } from '@mirage-projects/cw-commons';
import { User } from '@mirage-projects/cw-models';
import { CoreConfigurationService, UtilsPcvizService } from '@mirage-projects/cw-pcviz-core';
import { FullScreenLoaderService, MessageDialogOptions, MessageDialogService } from '@mirage-projects/cw-ui-components';

import { EnvironmentConfigurationService } from './core/services/environment-configuration.service';
import { MainNotificationService } from './core/services/main-notification.service';
import { CookieConsentService } from './services/cookie-consent/cookie-consent.service';

/**
 * Service that implements the common business logic for all application inputs: CW, Site and Embed.
 */
@Injectable({ providedIn: 'root' })
export class AppSharedService implements OnDestroy {
    loading = true;
    keepBackgroundVisible = false;
    isUserLogged = false;

    private changeDetectorRef!: ChangeDetectorRef;
    private onDestroy$: Subject<void> = new Subject();

    constructor(
        private translationService: TranslationService,
        private authorizationService: AuthorizationService,
        private utilsPcvizService: UtilsPcvizService,
        private fullScreenLoaderService: FullScreenLoaderService,
        private titleService: Title,
        private cookieConsentService: CookieConsentService,
        private router: Router,
        private messageDialogService: MessageDialogService,
        private environmentConfigurationService: EnvironmentConfigurationService,
        private coreConfigurationService: CoreConfigurationService,
        private magnetService: MagnetService,
        private entryPointService: EntryPointService,
        private mainNotificationService: MainNotificationService,
        private logService: LogService
    ) {}

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    init(changeDetectorRef: ChangeDetectorRef): void {
        this.changeDetectorRef = changeDetectorRef;
        this.fullScreenLoaderService.show();

        // Set app title when change lang
        this.translationService.onChangeLanguage$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.doTranslations();
        });

        this.fullScreenLoaderService.loading.pipe(takeUntil(this.onDestroy$)).subscribe((loading) => {
            this.loading = loading;
            this.keepBackgroundVisible = this.fullScreenLoaderService.keepBackgroundVisible;
            this.changeDetectorRef.detectChanges();
        });

        window.addEventListener('storage', (ev) => {
            if (this.authorizationService.isUserLogged() && ev.key === this.authorizationService.TOKEN_CW && ev.newValue) {
                const options: Partial<MessageDialogOptions> = {
                    headerText: 'WIZARD.SESSION_UPDATED',
                    contentText: 'WIZARD.SESSION_UPDATED_NOTICE',
                    buttonsList: [this.messageDialogService.getOkButtonConfig()],
                };

                this.messageDialogService.showWarning(options).then(() => {
                    this.router.navigate(['/']);
                });
            }
        });

        this.showMainNotifications();
    }

    /*
    Set user logged and load user acls
    */
    setUserLogged(user: Partial<User>): void {
        if (user.language) {
            this.translationService.controlLanguageUser(user.language);
        }
        this.authorizationService.setUserLogged(user);

        // Configure drivers core
        this.utilsPcvizService.configureDrivers(
            this.authorizationService.getToken(),
            this.authorizationService.getAuthType(),
            this.magnetService.isConnectingFromMagnet(),
            this.entryPointService.getEntryPointsForCore()
        );
        this.isUserLogged = true;
        this.coreConfigurationService.initCoreConfiguration(this.magnetService.getAppName(), this.magnetService.getEnvName());
        this.hideLoading();
    }

    /**
     * Removes the initial app's full screen loading
     */
    hideLoading(): void {
        this.fullScreenLoaderService.hide();
        if (this.environmentConfigurationService.getAppConfig().showCookieConsent !== false) {
            this.cookieConsentService.translate();
        }
    }

    private async showMainNotifications(): Promise<void> {
        const apiConfig = this.environmentConfigurationService.getAppConfig().API_CONFIG_CORE;
        if (!apiConfig.status) {
            return;
        }
        try {
            await this.mainNotificationService.checkAndShowNotificationsFile(`${apiConfig.status}/status.json`);
        } catch {
            this.logService.warn('Status file not found in server');
        }
    }

    private doTranslations(): void {
        this.titleService.setTitle(this.translationService.translate('APP.TITLE'));

        if (!this.loading) {
            this.cookieConsentService.translate();
        }
    }
}
